import React, { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const AccountPreferencesTab = ({ profile, onSave }) => {
  const [formData, setFormData] = useState({
    language: profile?.language || 'fr',
    timezone: profile?.timezone || 'Europe/Paris',
    currency: profile?.currency || 'EUR',
    notifications: profile?.notifications || {
      email: {
        newBookings: true,
        commissionUpdates: true,
        paymentReminders: true,
        marketingEmails: false,
        systemUpdates: true
      },
      sms: {
        urgentBookings: true,
        paymentConfirmations: true,
        systemAlerts: false
      },
      push: {
        realTimeUpdates: true,
        dailySummary: false,
        weeklyReports: true
      }
    },
    privacy: profile?.privacy || {
      profileVisibility: 'public',
      showContactInfo: true,
      showPerformanceStats: true,
      allowDirectMessages: true,
      showOnlineStatus: false
    },
    communication: profile?.communication || {
      preferredMethod: 'email',
      responseTime: '24h',
      availableHours: {
        start: '09:00',
        end: '18:00'
      },
      workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
    }
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const languageOptions = [
    { value: 'fr', label: 'Français' },
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'de', label: 'Deutsch' },
    { value: 'it', label: 'Italiano' }
  ];

  const timezoneOptions = [
    { value: 'Europe/Paris', label: 'Europe/Paris (CET)' },
    { value: 'Europe/London', label: 'Europe/London (GMT)' },
    { value: 'Europe/Berlin', label: 'Europe/Berlin (CET)' },
    { value: 'Europe/Madrid', label: 'Europe/Madrid (CET)' },
    { value: 'Europe/Rome', label: 'Europe/Rome (CET)' }
  ];

  const currencyOptions = [
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'USD', label: 'Dollar US ($)' },
    { value: 'GBP', label: 'Livre Sterling (£)' },
    { value: 'CHF', label: 'Franc Suisse (CHF)' }
  ];

  const communicationMethodOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Téléphone' },
    { value: 'sms', label: 'SMS' },
    { value: 'whatsapp', label: 'WhatsApp' }
  ];

  const responseTimeOptions = [
    { value: '1h', label: '1 heure' },
    { value: '4h', label: '4 heures' },
    { value: '24h', label: '24 heures' },
    { value: '48h', label: '48 heures' },
    { value: '72h', label: '72 heures' }
  ];

  const profileVisibilityOptions = [
    { value: 'public', label: 'Public - Visible par tous' },
    { value: 'partners', label: 'Partenaires - Visible par les partenaires uniquement' },
    { value: 'private', label: 'Privé - Visible par vous uniquement' }
  ];

  const workingDayOptions = [
    { value: 'monday', label: 'Lundi' },
    { value: 'tuesday', label: 'Mardi' },
    { value: 'wednesday', label: 'Mercredi' },
    { value: 'thursday', label: 'Jeudi' },
    { value: 'friday', label: 'Vendredi' },
    { value: 'saturday', label: 'Samedi' },
    { value: 'sunday', label: 'Dimanche' }
  ];

  const handleInputChange = (field, value) => {
    const fieldPath = field?.split('.');
    if (fieldPath?.length === 1) {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    } else if (fieldPath?.length === 2) {
      setFormData(prev => ({
        ...prev,
        [fieldPath?.[0]]: {
          ...prev?.[fieldPath?.[0]],
          [fieldPath?.[1]]: value
        }
      }));
    } else if (fieldPath?.length === 3) {
      setFormData(prev => ({
        ...prev,
        [fieldPath?.[0]]: {
          ...prev?.[fieldPath?.[0]],
          [fieldPath?.[1]]: {
            ...prev?.[fieldPath?.[0]]?.[fieldPath?.[1]],
            [fieldPath?.[2]]: value
          }
        }
      }));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      setIsEditing(false);
      // Update localStorage for language preference
      localStorage.setItem('language', formData?.language);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      language: profile?.language || 'fr',
      timezone: profile?.timezone || 'Europe/Paris',
      currency: profile?.currency || 'EUR',
      notifications: profile?.notifications || {
        email: {
          newBookings: true,
          commissionUpdates: true,
          paymentReminders: true,
          marketingEmails: false,
          systemUpdates: true
        },
        sms: {
          urgentBookings: true,
          paymentConfirmations: true,
          systemAlerts: false
        },
        push: {
          realTimeUpdates: true,
          dailySummary: false,
          weeklyReports: true
        }
      },
      privacy: profile?.privacy || {
        profileVisibility: 'public',
        showContactInfo: true,
        showPerformanceStats: true,
        allowDirectMessages: true,
        showOnlineStatus: false
      },
      communication: profile?.communication || {
        preferredMethod: 'email',
        responseTime: '24h',
        availableHours: {
          start: '09:00',
          end: '18:00'
        },
        workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
      }
    });
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Préférences du Compte</h2>
        {!isEditing ? (
          <Button
            variant="outline"
            onClick={() => setIsEditing(true)}
            iconName="Edit"
            iconPosition="left"
          >
            Modifier
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Annuler
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              loading={isSaving}
              iconName="Save"
              iconPosition="left"
            >
              Sauvegarder
            </Button>
          </div>
        )}
      </div>
      {/* General Settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Paramètres généraux</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            label="Langue"
            options={languageOptions}
            value={formData?.language}
            onChange={(value) => handleInputChange('language', value)}
            disabled={!isEditing}
          />

          <Select
            label="Fuseau horaire"
            options={timezoneOptions}
            value={formData?.timezone}
            onChange={(value) => handleInputChange('timezone', value)}
            disabled={!isEditing}
          />

          <Select
            label="Devise"
            options={currencyOptions}
            value={formData?.currency}
            onChange={(value) => handleInputChange('currency', value)}
            disabled={!isEditing}
          />
        </div>
      </div>
      {/* Notification Settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Notifications</h3>
        
        {/* Email Notifications */}
        <div className="bg-muted rounded-lg p-4">
          <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
            <Icon name="Mail" size={16} />
            Notifications par email
          </h4>
          <div className="space-y-3">
            <Checkbox
              label="Nouvelles réservations"
              checked={formData?.notifications?.email?.newBookings}
              onChange={(e) => handleInputChange('notifications.email.newBookings', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Mises à jour des commissions"
              checked={formData?.notifications?.email?.commissionUpdates}
              onChange={(e) => handleInputChange('notifications.email.commissionUpdates', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Rappels de paiement"
              checked={formData?.notifications?.email?.paymentReminders}
              onChange={(e) => handleInputChange('notifications.email.paymentReminders', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Emails marketing"
              checked={formData?.notifications?.email?.marketingEmails}
              onChange={(e) => handleInputChange('notifications.email.marketingEmails', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Mises à jour système"
              checked={formData?.notifications?.email?.systemUpdates}
              onChange={(e) => handleInputChange('notifications.email.systemUpdates', e?.target?.checked)}
              disabled={!isEditing}
            />
          </div>
        </div>

        {/* SMS Notifications */}
        <div className="bg-muted rounded-lg p-4">
          <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
            <Icon name="MessageSquare" size={16} />
            Notifications SMS
          </h4>
          <div className="space-y-3">
            <Checkbox
              label="Réservations urgentes"
              checked={formData?.notifications?.sms?.urgentBookings}
              onChange={(e) => handleInputChange('notifications.sms.urgentBookings', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Confirmations de paiement"
              checked={formData?.notifications?.sms?.paymentConfirmations}
              onChange={(e) => handleInputChange('notifications.sms.paymentConfirmations', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Alertes système"
              checked={formData?.notifications?.sms?.systemAlerts}
              onChange={(e) => handleInputChange('notifications.sms.systemAlerts', e?.target?.checked)}
              disabled={!isEditing}
            />
          </div>
        </div>

        {/* Push Notifications */}
        <div className="bg-muted rounded-lg p-4">
          <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
            <Icon name="Bell" size={16} />
            Notifications push
          </h4>
          <div className="space-y-3">
            <Checkbox
              label="Mises à jour en temps réel"
              checked={formData?.notifications?.push?.realTimeUpdates}
              onChange={(e) => handleInputChange('notifications.push.realTimeUpdates', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Résumé quotidien"
              checked={formData?.notifications?.push?.dailySummary}
              onChange={(e) => handleInputChange('notifications.push.dailySummary', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Rapports hebdomadaires"
              checked={formData?.notifications?.push?.weeklyReports}
              onChange={(e) => handleInputChange('notifications.push.weeklyReports', e?.target?.checked)}
              disabled={!isEditing}
            />
          </div>
        </div>
      </div>
      {/* Privacy Settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Confidentialité</h3>
        <div className="bg-muted rounded-lg p-4 space-y-4">
          <Select
            label="Visibilité du profil"
            options={profileVisibilityOptions}
            value={formData?.privacy?.profileVisibility}
            onChange={(value) => handleInputChange('privacy.profileVisibility', value)}
            disabled={!isEditing}
          />

          <div className="space-y-3">
            <Checkbox
              label="Afficher les informations de contact"
              checked={formData?.privacy?.showContactInfo}
              onChange={(e) => handleInputChange('privacy.showContactInfo', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Afficher les statistiques de performance"
              checked={formData?.privacy?.showPerformanceStats}
              onChange={(e) => handleInputChange('privacy.showPerformanceStats', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Autoriser les messages directs"
              checked={formData?.privacy?.allowDirectMessages}
              onChange={(e) => handleInputChange('privacy.allowDirectMessages', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Afficher le statut en ligne"
              checked={formData?.privacy?.showOnlineStatus}
              onChange={(e) => handleInputChange('privacy.showOnlineStatus', e?.target?.checked)}
              disabled={!isEditing}
            />
          </div>
        </div>
      </div>
      {/* Communication Preferences */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Préférences de communication</h3>
        <div className="bg-muted rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Méthode de communication préférée"
              options={communicationMethodOptions}
              value={formData?.communication?.preferredMethod}
              onChange={(value) => handleInputChange('communication.preferredMethod', value)}
              disabled={!isEditing}
            />

            <Select
              label="Temps de réponse attendu"
              options={responseTimeOptions}
              value={formData?.communication?.responseTime}
              onChange={(value) => handleInputChange('communication.responseTime', value)}
              disabled={!isEditing}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Heures de disponibilité
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={formData?.communication?.availableHours?.start}
                  onChange={(e) => handleInputChange('communication.availableHours.start', e?.target?.value)}
                  disabled={!isEditing}
                  className="px-3 py-2 border border-border rounded-md bg-background text-foreground disabled:bg-muted disabled:text-muted-foreground"
                />
                <span className="text-muted-foreground">à</span>
                <input
                  type="time"
                  value={formData?.communication?.availableHours?.end}
                  onChange={(e) => handleInputChange('communication.availableHours.end', e?.target?.value)}
                  disabled={!isEditing}
                  className="px-3 py-2 border border-border rounded-md bg-background text-foreground disabled:bg-muted disabled:text-muted-foreground"
                />
              </div>
            </div>

            <Select
              label="Jours de travail"
              options={workingDayOptions}
              value={formData?.communication?.workingDays}
              onChange={(value) => handleInputChange('communication.workingDays', value)}
              disabled={!isEditing}
              multiple
            />
          </div>
        </div>
      </div>
      {/* Account Actions */}
      <div className="bg-error/5 border border-error/20 rounded-lg p-4">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
          <Icon name="AlertTriangle" size={16} className="text-error" />
          Actions du compte
        </h3>
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
          >
            Exporter mes données
          </Button>
          <Button
            variant="outline"
            iconName="Pause"
            iconPosition="left"
          >
            Suspendre le compte
          </Button>
          <Button
            variant="destructive"
            iconName="Trash2"
            iconPosition="left"
          >
            Supprimer le compte
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mt-3">
          Ces actions sont irréversibles. Veuillez contacter le support si vous avez des questions.
        </p>
      </div>
    </div>
  );
};

export default AccountPreferencesTab;